import { Injectable } from '@angular/core';
import { ValidationErrors } from '@angular/forms';

/**
 * Service for generating consistent error messages for form field validation.
 * Supports both standard Angular validators and custom validators.
 */
@Injectable()
export class FieldErrorData {
  /**
   * Priority order for displaying validation errors.
   * When multiple errors exist, the first error in this list will be displayed.
   */
  private readonly _errorPriorityOrder = [
    'serverError',  // Server errors have highest priority
    'required',
    'email',
    'minlength',
    'maxlength',
    'min',
    'max',
    'pattern',
  ];

  /**
   * Generate an error message for the given validation errors.
   *
   * @param errors - ValidationErrors object from FormControl
   * @param label - Field label to use in error messages
   * @param customMessages - Custom error messages by validator key
   * @param constraints - Field constraints (maxlength, minlength, min, max)
   * @returns Error message string or null if no errors
   */
  public getErrorMessage(
    errors: ValidationErrors,
    label: string,
    customMessages: Partial<Record<string, string>>,
    constraints: {
      maxlength?: number | null;
      minlength?: number | null;
      min?: number | null;
      max?: number | null;
    }
  ): string | null {
    const errorKey = this._getFirstErrorKey(errors);
    if (!errorKey) {
      return null;
    }

    // Check for custom message first
    if (customMessages[errorKey]) {
      return customMessages[errorKey]!;
    }

    // Generate default message
    return this._generateDefaultMessage(errorKey, errors[errorKey], label, constraints);
  }

  /**
   * Get the first error key based on priority order.
   *
   * @param errors - ValidationErrors object
   * @returns First error key or null if no errors
   */
  private _getFirstErrorKey(errors: ValidationErrors): string | null {
    // Check priority errors first
    for (const key of this._errorPriorityOrder) {
      if (errors[key]) {
        return key;
      }
    }

    // Return first custom validator error (alphabetical)
    const customKeys = Object.keys(errors).sort();
    return customKeys[0] || null;
  }

  /**
   * Generate a default error message for standard validators.
   *
   * @param key - Validator key
   * @param errorValue - Error value from validator
   * @param label - Field label
   * @param constraints - Field constraints
   * @returns Generated error message
   */
  private _generateDefaultMessage(
    key: string,
    errorValue: unknown,
    label: string,
    constraints: {
      maxlength?: number | null;
      minlength?: number | null;
      min?: number | null;
      max?: number | null;
    }
  ): string {
    switch (key) {
      case 'serverError':
        // Server error value is the message itself
        return typeof errorValue === 'string' ? errorValue : `${label} has an error`;

      case 'required':
        return `${label} is required`;

      case 'maxlength': {
        const maxLen = constraints.maxlength || (errorValue as { requiredLength: number }).requiredLength;
        return `${label} cannot exceed ${maxLen} characters`;
      }

      case 'minlength': {
        const minLen = constraints.minlength || (errorValue as { requiredLength: number }).requiredLength;
        return `${label} must be at least ${minLen} characters`;
      }

      case 'email':
        return 'Please enter a valid email address';

      case 'min': {
        const minVal = constraints.min ?? (errorValue as { min: number }).min;
        return `${label} must be at least ${minVal}`;
      }

      case 'max': {
        const maxVal = constraints.max ?? (errorValue as { max: number }).max;
        return `${label} must be at most ${maxVal}`;
      }

      case 'pattern':
        return `${label} has an invalid format`;

      default:
        return `${label} is invalid`;
    }
  }
}
